'use client'
import { LineChevronRightSmall } from '@qr-front/icons/line-chevron-right-small'
import { LineGlobe } from '@qr-front/icons/line-globe'
import { SolidFacebook } from '@qr-front/icons/solid-facebook'
import { buttonCls } from '@qr-front/ui/components/Button'
import { cn } from '@qr-front/ui/utils'
import { useFrameFontSize } from 'features/generator/services/client'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslations } from 'next-intl'

import { addMissingUrlProtocol } from '../../../../../utils/urlUtils'
import {
	facebookButtonListAtom,
	facebookNameAtom,
	facebookTitleAtom,
	facebookUrlAtom,
	facebookWebsiteAtom,
} from '../../../contentFormSections'
import { TFacebookButton } from '../../../contentFormSections/FacebookForm/FacebookInfoFormModel'
import { openOverlayIdAtom } from '../../../generatorState'
import { QrScreenOverlay } from '../../../QrScreen'
import { QrScreen, TQrScreenProps } from '../../../QrScreen/QrScreen'
import { useColoredButton } from '../../hooks/useColoredButton'
import { CarouselPreview } from './CarouselPreview'
import {
	facebookPrimaryColorAtom,
	facebookSecondaryColorAtom,
} from './facebookScreenState'
import { ImageGallery } from './ImageGallery'

const Header = ({ demoMode }: TQrScreenProps) => {
	const t = useTranslations()
	const name = useAtomValue(facebookNameAtom)
	const title = useAtomValue(facebookTitleAtom)
	const website = useAtomValue(facebookWebsiteAtom)
	const displayedName = demoMode
		? t('generator__facebook_screen__name_fallback')
		: name
	const displayedTitle = demoMode
		? t('generator__facebook_screen__title_fallback')
		: title
	const displayedWebsite = demoMode
		? t('generator__facebook_screen__website_fallback')
		: website

	return (
		<div className="flex flex-col items-center px-5 pb-3.5 pt-6 transition-colors">
			<h1 className="type-heading-4 max-w-full break-words text-center transition-colors">
				{displayedName}
			</h1>
			<h2 className="max-w-full break-words text-center text-10 uppercase leading-[1rem] transition-colors">
				{displayedTitle}
			</h2>
			{displayedWebsite && (
				<a
					href={addMissingUrlProtocol(displayedWebsite)}
					className="mt-4 max-w-full space-x-2 text-center text-[10px] leading-[16px] transition-colors"
					target="_blank"
					rel="noreferrer"
				>
					<LineGlobe className="float-start size-4 shrink-0" />
					<span className="break-words">{displayedWebsite}</span>
				</a>
			)}
		</div>
	)
}

const CustomButton = ({
	button: { title, url },
	previewMode,
	demoMode,
}: {
	button: TFacebookButton
	previewMode: boolean
	demoMode: boolean
}) => {
	const t = useTranslations()
	const secondaryColor = useAtomValue(facebookSecondaryColorAtom)
	const { coloredButtonCls, coloredButtonStyle } =
		useColoredButton(secondaryColor)

	const displayedTitle = demoMode
		? t(
				'generator__content_form_section__images_information__button_text__placeholder',
			)
		: title

	const fontSize = useFrameFontSize({
		text: title,
		maxWidth: 220,
		maxSize: previewMode || demoMode ? 12 : 15,
		fontFamily: 'var(--font-body)',
	})

	const style = {
		fontSize: `${fontSize}px`,
		...coloredButtonStyle,
	}

	const baseButtonCls = cn(
		buttonCls({ visual: 'secondary' }),
		'w-full grow text-body-caption font-normal',
	)

	if (!url) {
		return (
			<span className={cn(baseButtonCls, 'cursor-not-allowed')} style={style}>
				{displayedTitle}
			</span>
		)
	}

	return (
		<a
			href={addMissingUrlProtocol(url)}
			className={cn(baseButtonCls, coloredButtonCls)}
			target="_blank"
			style={style}
			rel="noreferrer"
		>
			{displayedTitle}
		</a>
	)
}

const DemoButton = () => {
	const t = useTranslations()
	const { coloredButtonCls, coloredButtonStyle } = useColoredButton('black')
	return (
		<div
			className={cn(
				buttonCls({ visual: 'secondary' }),
				coloredButtonCls,
				'grow rounded-6 text-body-caption font-normal',
			)}
			style={coloredButtonStyle}
		>
			{t('generator__facebook_screen__demo_mode_button_placeholder')}
		</div>
	)
}

const Buttons = ({
	previewMode,
	demoMode,
}: {
	previewMode: boolean
	demoMode: boolean
}) => {
	const buttonList = useAtomValue(facebookButtonListAtom)
	const firstButton = buttonList[0]
	const additionalButtonList = buttonList.slice(1)

	return (
		<div className="mx-auto flex max-w-qr-screen-constrained flex-col gap-2 px-5">
			<div className="flex flex-nowrap gap-2">
				{demoMode ? <DemoButton /> : null}
				{firstButton ? (
					<CustomButton
						button={firstButton}
						previewMode={previewMode}
						demoMode={demoMode}
					/>
				) : null}
			</div>
			{additionalButtonList.map((button, index) => (
				<CustomButton
					key={index}
					button={button}
					previewMode={previewMode}
					demoMode={demoMode}
				/>
			))}
		</div>
	)
}

const Facebook = ({ demoMode }: { demoMode: boolean }) => {
	const t = useTranslations()
	const facebookUrl = useAtomValue(facebookUrlAtom)
	if (!facebookUrl && !demoMode) return null

	return (
		<div className="mx-auto flex max-w-qr-screen-constrained flex-col gap-2 px-5">
			<a
				href={addMissingUrlProtocol(facebookUrl ?? 'facebook.com')}
				className="type-body-small mt-2 flex h-14 grow items-center gap-3 rounded-6 bg-light-grey-70 py-2 pl-3 pr-2 font-semibold"
				target="_blank"
				rel="noreferrer"
			>
				<div className="rounded-full">
					<SolidFacebook className="size-8 text-[#039BE5]" />
				</div>
				{t('public__qr_types_section__facebook')}
				<LineChevronRightSmall className="ml-auto size-4 text-[#202023] transition-transform duration-200" />
			</a>
		</div>
	)
}

const Content = ({ previewMode, demoMode }: TQrScreenProps) => {
	const setOpenOverlayId = useSetAtom(openOverlayIdAtom)

	return (
		<>
			<QrScreenOverlay
				id="image-gallery"
				previewMode={previewMode}
				className="z-[1] bg-generator-bg"
			>
				<ImageGallery hideOverlay={() => setOpenOverlayId(null)} />
			</QrScreenOverlay>
			<div className="pb-6">
				<CarouselPreview
					demoMode={demoMode}
					showOverlay={() => {
						setOpenOverlayId('image-gallery')
					}}
				/>
				<Header previewMode={previewMode} demoMode={demoMode} />
				<Buttons demoMode={demoMode} previewMode={previewMode} />
				<Facebook demoMode={demoMode} />
			</div>
		</>
	)
}

export const FacebookScreen = (props: TQrScreenProps) => (
	<QrScreen
		{...props}
		primaryColor={useAtomValue(facebookPrimaryColorAtom)}
		content={<Content {...props} />}
	/>
)
